import React, { useState } from 'react';
import { X, Trash2, AlertTriangle, MessageSquare } from 'lucide-react';
import api from '../../utils/api';

function DeletePhoneModal({ phone, notificationCount, onClose, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleDelete() {
    setError('');
    setSubmitting(true);
    try {
      const resp = await api.delete(`/api/sms/phones/${phone.id}`);

      if (resp.data?.success) {
        onSuccess();
      } else {
        setError('Failed to remove phone number');
      }
    } catch (err) {
      console.error('Error deleting phone:', err);
      setError(err.response?.data?.error || 'Failed to remove phone number');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 flex-shrink-0">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Remove Phone Number</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="font-medium text-gray-900">{phone.phone_name}</div>
            <div className="text-sm text-gray-600">{phone.phone_number}</div>
          </div>

          {notificationCount > 0 && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-amber-900 mb-1">
                    This will affect your notifications
                  </div>
                  <div className="text-sm text-amber-800">
                    {notificationCount} notification{notificationCount !== 1 ? 's' : ''} use{notificationCount === 1 ? 's' : ''} this phone number. 
                    {notificationCount === 1 ? ' It' : ' They'} will be disabled when you remove this phone.
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="text-sm text-gray-600">
            Are you sure you want to remove this phone number? This action cannot be undone.
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 sm:py-2 px-4 min-h-[44px] border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={submitting}
              className="flex-1 py-2.5 sm:py-2 px-4 min-h-[44px] bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Removing...' : 'Remove Phone'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeletePhoneModal;
